import { useState, useEffect } from 'react';
import RenderFormWithSteps from './RenderFormWithSteps';
import RenderFormNoSteps from './RenderFormNoSteps';
import { validateFormsFields } from '../../helpers/validationHelper';
import {
    getStatusPermissions,
    canPerformAction,
    getStatusMessage,
    getDisplayStatus,
    APPLICATION_STATUS
} from '../../helpers/statusManager';

const DynamicForm = ({ schema, onSubmit, onSaveDraft, initialValues, stepperOn, viewOnly }) => {
    const [formData, setFormData] = useState(initialValues || {});
    const [formErrors, setFormErrors] = useState({});
    const [isStepper, setIsStepper] = useState(stepperOn || false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track unsaved changes
    
    // Status-based permissions
    const applicationStatus = formData.status || APPLICATION_STATUS.DRAFT;
    const statusPermissions = getStatusPermissions(applicationStatus);
    const statusMessage = getStatusMessage(applicationStatus, formData);
    
    // Determine if form should be read-only based on status or explicit viewOnly prop
    const isReadOnly = viewOnly || statusPermissions.isReadOnly;

    // Update formData when initialValues changes (e.g., after save draft refresh)
    useEffect(() => {
        if (initialValues && Object.keys(initialValues).length > 0) {
            // Always update formData with the latest initialValues to ensure consistency
            // This is crucial for maintaining correct file IDs after save operations
            setFormData(initialValues);
            
            // Reset unsaved changes flag since we're syncing with the server
            setHasUnsavedChanges(false);
        }
    }, [JSON.stringify(initialValues)]);

    useEffect(() => {
        setIsStepper(stepperOn);
    }, [stepperOn]);

    const handleChange = (e) => {
        if (isReadOnly) return; // Prevent changes in read-only mode
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        // Track changes    
        setHasUnsavedChanges(true);

        // Clear field-specific error when the field is updated and valid
        setFormErrors((prevErrors) => {
            let updatedErrors = { ...prevErrors };
            
            if (fieldValue) {
                // Clear error if value is present
                updatedErrors[name] = '';
                
                // For file upload fields (ending with "Id" or "Name"), 
                // also clear the error for the base field name
                if (name.endsWith('Id') || name.endsWith('Name')) {
                    const baseFieldName = name.replace(/(Id|Name)$/, '');
                    updatedErrors[baseFieldName] = '';
                }
            }
            
            return updatedErrors;
        });

        // Clear specialization when education type changes
        let updatedFormData = {
            ...formData,
            [name]: fieldValue,
        };

        // If changing education type, clear the specialization
        if (name === 'educationType') {
            updatedFormData.specialization = '';
        }

        setFormData(updatedFormData);
    };

    // Function to handle multiple field updates at once (for file uploads)
    const handleMultipleFieldsChange = (fields) => {
        if (isReadOnly) return;

        setHasUnsavedChanges(true);

        // Clear errors for all updated fields
        setFormErrors((prevErrors) => {
            let updatedErrors = { ...prevErrors };
            
            Object.entries(fields).forEach(([name, value]) => {
                if (value) {
                    updatedErrors[name] = '';
                    if (name.endsWith('Id') || name.endsWith('Name')) {
                        const baseFieldName = name.replace(/(Id|Name)$/, '');
                        updatedErrors[baseFieldName] = '';
                    }
                }
            });
            
            return updatedErrors;
        });

        // Update multiple fields at once
        const updatedFormData = {
            ...formData,
            ...fields
        };

        setFormData(updatedFormData);
        
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Check if submission is allowed for current status
        if (!canPerformAction(applicationStatus, 'submit')) {
            alert(`Cannot submit application. Current status: ${applicationStatus}. ${statusMessage.text}`);
            return;
        }
        
        if (isReadOnly) {
            alert('Cannot submit application in read-only mode');
            return;
        }

        // Store original status for concurrent change validation
        const submissionData = {
            ...formData,
            originalStatus: applicationStatus
        };
        
        onSubmit(submissionData);
    };

    const handleSaveDraft = (e) => {
        e.preventDefault();
        
        // Check if save draft is allowed for current status
        if (!canPerformAction(applicationStatus, 'saveDraft')) {
            alert(`Cannot save draft. Current status: ${applicationStatus}. ${statusMessage.text}`);
            return;
        }
        
        if (isReadOnly) {
            alert('Cannot save changes in read-only mode');
            return;
        }

        //form already validate in form Component.
        if (onSaveDraft) {
            onSaveDraft(formData);
        } else {
            onSubmit(formData);
        }
        setHasUnsavedChanges(false); // Reset change tracking
    };

    const validateForm = () => {
        let errors = {};
        let formIsValid = true;

        schema.forEach((tab) => {
            if (tab.fields) {
                tab.fields.forEach((field) => {
                    let fieldValue = formData[field.name];
                    
                    // Special handling for file upload fields
                    if (field.type === 'file') {
                        const fileIdKey = field.name + "Id";
                        const fileNameKey = field.name + "Name";
                        // For file uploads, consider the field filled if either ID or Name exists
                        fieldValue = formData[fileIdKey] || formData[fileNameKey];
                    }
                    
                    if (field.required && !fieldValue) {
                        formIsValid = false;
                        errors[field.name] = 'This field is required';
                    } else if (fieldValue && field.type !== 'file') {
                        const { isValid, errorMessage } = validateFormsFields(field, fieldValue);
                        if (!isValid) {
                            formIsValid = isValid;
                            errors[field.name] = errorMessage;
                        }
                    }
                });
            }
        });

        setFormErrors(errors);
        return formIsValid;
    };

    const validateTabFields = (currentTab) => {
        let errors = {};
        let tabIsValid = true;

        if (currentTab?.sections) {
            currentTab.sections.forEach((section) => {
                section.fields.forEach((field) => {
                    let fieldValue = formData[field.name];
                    
                    // Special handling for file upload fields
                    if (field.type === 'file') {
                        const fileIdKey = field.name + "Id";
                        const fileNameKey = field.name + "Name";
                        // For file uploads, consider the field filled if either ID or Name exists
                        fieldValue = formData[fileIdKey] || formData[fileNameKey];
                    }
                    
                    if (field.required && !fieldValue) {
                        tabIsValid = false;
                        errors[field.name] = 'This field is required';
                    } else if (fieldValue && field.type !== 'file') {
                        const { isValid, errorMessage } = validateFormsFields(field, fieldValue);
                        if (!isValid) {
                            tabIsValid = isValid;
                            errors[field.name] = errorMessage;
                        }
                    }
                });
            });
        } else if (currentTab?.fields) {
            currentTab.fields.forEach((field) => {
                let fieldValue = formData[field.name];
                
                // Special handling for file upload fields
                if (field.type === 'file') {
                    const fileIdKey = field.name + "Id";
                    const fileNameKey = field.name + "Name";
                    // For file uploads, consider the field filled if either ID or Name exists
                    fieldValue = formData[fileIdKey] || formData[fileNameKey];
                }
                
                if (field.required && !fieldValue) {
                    tabIsValid = false;
                    errors[field.name] = 'This field is required';
                } else if (fieldValue && field.type !== 'file') {
                    const { isValid, errorMessage } = validateFormsFields(field, fieldValue);
                    if (!isValid) {
                        tabIsValid = isValid;
                        errors[field.name] = errorMessage;
                    }
                }
            });
        }

        setFormErrors((prevErrors) => ({ ...prevErrors, ...errors }));
        return tabIsValid;
    };

    return (
        <>
            {/* Status Message Display */}
            {applicationStatus !== APPLICATION_STATUS.DRAFT && (
                <div className={`alert alert-${statusMessage.type} mb-3`} role="alert">
                    <i className={`${statusMessage.icon} me-2`}></i>
                    <strong>Status: {getDisplayStatus(applicationStatus)}</strong>
                    <div className="mt-1">{statusMessage.text}</div>
                </div>
            )}
            
            {isStepper ? (
                <RenderFormWithSteps
                    schema={schema}
                    formData={formData}
                    formErrors={formErrors}
                    handleChange={handleChange}
                    validateTabFields={validateTabFields}
                    handleSubmit={handleSubmit}
                    handleSaveDraft={handleSaveDraft}
                    onSaveDraft={onSaveDraft}
                    hasUnsavedChanges={hasUnsavedChanges}
                    viewOnly={isReadOnly} // Pass computed read-only state
                    statusPermissions={statusPermissions} // Pass status permissions
                    applicationStatus={applicationStatus} // Pass current status
                />
            ) : (
                <RenderFormNoSteps
                    schema={schema}
                    formData={formData}
                    formErrors={formErrors}
                    handleChange={handleChange}
                    handleMultipleFieldsChange={handleMultipleFieldsChange}
                    validateForm={validateForm}
                    handleSubmit={handleSubmit}
                    viewOnly={isReadOnly} // Pass computed read-only state
                    statusPermissions={statusPermissions} // Pass status permissions
                    applicationStatus={applicationStatus} // Pass current status
                />
            )}
        </>
    );
};

export default DynamicForm;
